class HomeReviews extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector(".pr__cards");
    this.cards = [...this.querySelectorAll(".pr__card")];
    this.dotsContainer = this.querySelector(".pr__dots");
    this.prevBtn = this.querySelector(".pr__arrow--prev");
    this.nextBtn = this.querySelector(".pr__arrow--next");
    this.dots = [];
    this.pageCount = 0;
    this.currentPage = 0;

    this.setupReadMore();
    this.setupArrows();
    this.buildDots();
    this.setupScrollListener();

    const ro = new ResizeObserver(() => this.buildDots());
    ro.observe(this.track);
  }

  getVisibleCount() {
    if (!this.track || !this.cards.length) return 1;
    const trackWidth = this.track.clientWidth;
    const cardWidth = this.cards[0].offsetWidth;
    if (!cardWidth) return 1;
    return Math.max(1, Math.round(trackWidth / cardWidth));
  }

  buildDots() {
    if (!this.dotsContainer || !this.cards.length) return;

    const visible = this.getVisibleCount();
    const pages = Math.max(1, this.cards.length - visible + 1);

    if (pages === this.pageCount) {
      this.syncActiveDot();
      return;
    }

    this.pageCount = pages;
    this.dotsContainer.innerHTML = "";
    this.dots = [];

    if (pages <= 1) {
      this.dotsContainer.style.display = "none";
      return;
    }

    this.dotsContainer.style.display = "";

    for (let i = 0; i < pages; i++) {
      const btn = document.createElement("button");
      btn.className = "pr__dot" + (i === 0 ? " pr__dot--active" : "");
      btn.type = "button";
      btn.setAttribute("aria-label", `Page ${i + 1}`);
      btn.addEventListener("click", () => this.goToPage(i));
      this.dotsContainer.appendChild(btn);
      this.dots.push(btn);
    }

    this.syncActiveDot();
    this.updateArrows();
  }

  goToPage(pageIndex) {
    if (!this.track) return;
    const targetCard = this.cards[pageIndex];
    if (!targetCard) return;

    this.track.scrollTo({
      left: targetCard.offsetLeft - this.track.offsetLeft,
      behavior: "smooth",
    });
  }

  setupScrollListener() {
    if (!this.track) return;

    let ticking = false;
    this.track.addEventListener("scroll", () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        this.syncActiveDot();
        ticking = false;
      });
    }, { passive: true });
  }

  syncActiveDot() {
    if (!this.track || !this.dots.length || !this.cards.length) return;

    const scrollLeft = this.track.scrollLeft;
    const trackLeft = this.track.offsetLeft;

    let closestIdx = 0;
    let minDist = Infinity;

    this.cards.forEach((card, i) => {
      const dist = Math.abs(card.offsetLeft - trackLeft - scrollLeft);
      if (dist < minDist) {
        minDist = dist;
        closestIdx = i;
      }
    });

    const activePage = Math.min(closestIdx, this.dots.length - 1);

    if (activePage !== this.currentPage) {
      this.currentPage = activePage;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle("pr__dot--active", i === activePage);
      });
      this.updateArrows();
    }
  }

  setupArrows() {
    this.prevBtn?.addEventListener("click", () => {
      if (this.currentPage > 0) this.goToPage(this.currentPage - 1);
    });
    this.nextBtn?.addEventListener("click", () => {
      if (this.currentPage < this.pageCount - 1) this.goToPage(this.currentPage + 1);
    });
  }

  updateArrows() {
    if (!this.prevBtn || !this.nextBtn) return;
    this.prevBtn.disabled = this.currentPage <= 0;
    this.nextBtn.disabled = this.currentPage >= this.pageCount - 1;
  }

  setupReadMore() {
    this.cards.forEach((card) => {
      const crop = card.querySelector(".pr__review-body--crop p");
      const full = card.querySelector(".pr__review-body--full p");
      const controls = card.querySelector(".pr__card-toggle");
      const moreBtn = card.querySelector(".pr__toggle-btn--more");
      const lessBtn = card.querySelector(".pr__toggle-btn--less");

      if (!crop || !full || !controls || !moreBtn || !lessBtn) return;
      if (crop.textContent.trim() === full.textContent.trim()) return;

      controls.classList.remove("hidden");

      moreBtn.addEventListener("click", () => {
        card.querySelector(".pr__review-body--crop")?.classList.add("hidden");
        card.querySelector(".pr__review-body--full")?.classList.remove("hidden");
        moreBtn.classList.add("hidden");
        lessBtn.classList.remove("hidden");
      });

      lessBtn.addEventListener("click", () => {
        card.querySelector(".pr__review-body--crop")?.classList.remove("hidden");
        card.querySelector(".pr__review-body--full")?.classList.add("hidden");
        lessBtn.classList.add("hidden");
        moreBtn.classList.remove("hidden");
      });
    });
  }
}

customElements.define("home-reviews", HomeReviews);
